import React, { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { basicSearchProfiles, fetchProfilesBySkillCategory, fetchRelevantProfiles, fetchRolesBySkill, filterByRole, getRelevantProfilesV2Public, queryFilteredProfiles } from "../../../redux/actions/halloffame";
import { X, Filter, ChartNoAxesColumnIncreasing, Search, TrendingUp } from "lucide-react";
import { RESET_SELECTED_FILTERS, SET_SELECTED_FILTERS } from "../../../redux/actions/types";
import { Mixpanel } from "../../../config/Mixpanel";
import Constants from "../../../common/constants";
import RecruiterAuth from "../RecruiterAuth";
import RecruiterDetails from "../RecruiterAuth/RecruiterDetails";
import ListofCategories from "./ListofCategories";
import StickyHeader from "./StickyHeader";
import CandidatesCard from "./CandidatesCard";
import { useSearchParams, useLocation } from "react-router-dom";
import Locations from "./Locations";
import RowCandidateCard from "./RowCandidateCard";
import FeaturedLeaderboard from "../RecruiterLandingPage/FeaturedLeaderboard";
import SkillModal from "./SkillModal";
import ProfilesLoader from "./ProfilesLoader";

const normalizeValue = (value) => String(value || "").trim().toLowerCase();
const normalizeArray = (values) =>
  Array.isArray(values)
    ? values.map((value) => normalizeValue(value)).filter(Boolean)
    : [];

const profileMatchesExperience = (profile, selectedExperience) => {
  if (!selectedExperience?.length) return true;

  const totalMonths = profile?.additional_profile_info?.total_experience_months;
  const experienceCategory = normalizeValue(profile?.additional_profile_info?.total_experience_category);

  if (typeof totalMonths === "number" && !Number.isNaN(totalMonths)) {
    const years = totalMonths / 12;
    return selectedExperience.some((range) => {
      const normalizedRange = normalizeValue(range);
      if (!normalizedRange) return false;

      const plusMatch = normalizedRange.match(/^(\d+)\+\s*years$/);
      if (plusMatch) {
        return years >= Number(plusMatch[1]);
      }

      const rangeMatch = normalizedRange.match(/^(\d+)\s*-\s*(\d+)\s*years$/);
      if (rangeMatch) {
        const minYears = Number(rangeMatch[1]);
        const maxYears = Number(rangeMatch[2]);
        return years >= minYears && years <= maxYears;
      }

      return experienceCategory === normalizedRange;
    });
  }

  return selectedExperience.some((range) => experienceCategory === normalizeValue(range));
};

const filterProfilesClientSide = (profiles, filters) => {
  if (!Array.isArray(profiles) || profiles.length === 0) return [];

  const companies = normalizeArray(filters?.companies);
  const institutions = normalizeArray(filters?.institutions);
  const states = normalizeArray(filters?.states);
  const cities = normalizeArray(filters?.cities);
  const skills = normalizeArray(filters?.skills);
  const experience = normalizeArray(filters?.experience);

  const hasActiveFilters =
    companies.length || institutions.length || states.length || cities.length || skills.length || experience.length;

  if (!hasActiveFilters) return profiles;

  return profiles.filter((profile) => {
    const companyName = normalizeValue(profile?.experience?.company_name);
    const institutionName = normalizeValue(profile?.education?.institution_name);
    const cityCandidates = [
      profile?.profile?.current_location?.city,
      profile?.experience?.company_location?.city,
      profile?.education?.location?.city,
    ]
      .map((value) => normalizeValue(value))
      .filter(Boolean);
    const stateCandidates = [
      profile?.profile?.current_location?.state,
      profile?.experience?.company_location?.state,
      profile?.education?.location?.state,
    ]
      .map((value) => normalizeValue(value))
      .filter(Boolean);
    const skillCandidates = [
      ...Object.keys(profile?.skill_scores || {}),
      ...(profile?.experience?.technical_skills || []),
      ...(profile?.profile?.key_strengths || []),
    ]
      .map((value) => normalizeValue(value))
      .filter(Boolean);

    const matchesCompanies = companies.length === 0 || companies.some((company) => companyName.includes(company));
    const matchesInstitutions =
      institutions.length === 0 || institutions.some((institution) => institutionName.includes(institution));
    const matchesStates =
      states.length === 0 ||
      states.some((state) => stateCandidates.some((candidate) => candidate.includes(state) || state.includes(candidate)));
    const matchesCities =
      cities.length === 0 ||
      cities.some((city) => cityCandidates.some((candidate) => candidate.includes(city) || city.includes(candidate)));
    const matchesSkills =
      skills.length === 0 ||
      skills.some((selectedSkill) =>
        skillCandidates.some(
          (candidateSkill) => candidateSkill.includes(selectedSkill) || selectedSkill.includes(candidateSkill),
        ),
      );
    const matchesExperience = profileMatchesExperience(profile, experience);

    return (
      matchesCompanies &&
      matchesInstitutions &&
      matchesStates &&
      matchesCities &&
      matchesSkills &&
      matchesExperience
    );
  });
};

const LeaderboardProfiles = () => {
  const dispatch = useDispatch();
  const [relevantProfiles, setRelevantProfiles] = useState([]);
  const [basicSearchProfilesList, setBasicSearchProfilesList] = useState([]);
  const [searchQueryProfiles, setSearchQueryProfiles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isRecruiterAuthOpen, setIsRecruiterAuthOpen] = useState(false);
  const [isRecruiterDetailsOpen, setIsRecruiterDetailsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [isSkillModalOpen, setIsSkillModalOpen] = useState(false);
  const [isQueryReset, setIsQueryReset] = useState(false);
  const [showStickyFilters, setShowStickyFilters] = useState(false);
  const filterButtonRef = useRef(null);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [nlQuery, setNlQuery] = useState("");
  const [width, setWidth] = useState(window.innerWidth);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [candidateRoles, setCandidateRoles] = useState([]);

  const recruiterAuthData = useSelector((state) => state.hallOfFame.recruiterAuthData);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (recruiterAuthData) {
      if (recruiterAuthData.onboarding_required) {
        setIsRecruiterDetailsOpen(true);
      }
    }
  }, [recruiterAuthData]);

  useEffect(() => {
    Mixpanel.track(Constants.Mixpanel.RECRUITER_RESULTS_PAGE_LOADED);
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyFilters(!entry.isIntersecting && entry.boundingClientRect.top < 200);
      },
      {
        threshold: 0,
        rootMargin: "-80px 0px 0px 0px"
      }
    );

    const currentFilterRef = filterButtonRef.current;
    if (currentFilterRef) {
      observer.observe(currentFilterRef);
    }

    return () => {
      if (currentFilterRef) {
        observer.unobserve(currentFilterRef);
      }
    };
  }, []);

  const recruiterUUID = useSelector((store) => store?.hallOfFame?.recruiterUUID);
  const selectedFilters = useSelector((store) => store?.hallOfFame?.selectedFilters);
  const selectedFiltersList = useSelector((store) => store?.hallOfFame?.selectedFilters);
  const recruiterAccessToken = useSelector((store) => store?.hallOfFame?.recruiterAccessToken);
  const recruiterPublicNlSearchUsed = useSelector((store) => store?.hallOfFame?.recruiterPublicNlSearchUsed);
  const recruiterPublicNlSearchUsedRef = useRef(recruiterPublicNlSearchUsed);
  recruiterPublicNlSearchUsedRef.current = recruiterPublicNlSearchUsed;
  const searchQuery = getRawQuery();
  const skill = searchParams.get("skill");
  const applyFiltersFromLandingPage = searchParams.get("applyFilters");

  useEffect(() => {
    setNlQuery(searchQuery || skill || "");
  }, [searchQuery, skill]);

  const seeMoreProfiles = () => {
    Mixpanel.track(Constants.Mixpanel.RECRUITER_RESULTS_PAGE_SEE_MORE_CLICKED);
    const nextPage = page + 1;
    setPage(nextPage);
    setIsMoreLoading(true);
    const onSuccess = (response) => {
      setRelevantProfiles(prev => [...prev, ...(response?.profiles || [])]);
      setIsMoreLoading(false);
    };
    const onError = (error) => {
      if (error?.response?.status === 401) {
        setIsMoreLoading(false);
        setIsRecruiterAuthOpen(true);
        return;
      }
      console.log(error);
      setIsMoreLoading(false);
    };

    // When a role chip is selected, paginate using the role-filter API.
    if (selectedRole) {
      dispatch(
        filterByRole(skill, selectedRole, nextPage, pageSize, recruiterUUID, onSuccess, onError),
      );
      return;
    }

    dispatch(
      fetchProfilesBySkillCategory(recruiterUUID, skill, nextPage, pageSize, onSuccess, onError),
    );
  }

  const handleQueryReset = () => {
    Mixpanel.track(Constants.Mixpanel.RECRUITER_RESULTS_PAGE_QUERY_RESET_CLICKED);
    setIsQueryReset(true);
    dispatch({ type: RESET_SELECTED_FILTERS })
    setRelevantProfiles([]);
    setSelectedRole(null);
    setNlQuery("");
    setSearchParams({});
  }

  const onApplyFilters = useCallback((filtersOverride) => {
    Mixpanel.track(Constants.Mixpanel.RECRUITER_FILTER_DRAWER_APPLY_FILTERS_SUBMITTED);
    setIsFilterOpen(false);
    setIsQueryReset(false);
    setPage(1);
    setSelectedRole(null);

    const filtersToUse = (filtersOverride && !filtersOverride.nativeEvent && !filtersOverride.type)
      ? filtersOverride
      : selectedFilters;

    if (
      searchQuery &&
      (recruiterAccessToken || searchQueryProfiles.length > 0)
    ) {
      setIsLoading(true);
      const filteredProfiles = filterProfilesClientSide(searchQueryProfiles, filtersToUse);
      setRelevantProfiles(filteredProfiles);
      setTotalCount(filteredProfiles.length);
      setIsLoading(false);
      scrollToTop();
      return;
    }

    if (!recruiterAccessToken) {
      setIsLoading(false);
      setIsRecruiterAuthOpen(true);
      return;
    }

    setIsLoading(true);

    dispatch(queryFilteredProfiles(recruiterAccessToken,
      filtersToUse?.companies || [],
      filtersToUse?.institutions || [],
      filtersToUse?.states || [],
      filtersToUse?.cities || [],
      filtersToUse?.skills || [],
      filtersToUse?.experience || [],
      1,
      pageSize,
      (response) => {
        setRelevantProfiles(response?.profiles || []);
        setTotalCount(response?.total_count);
        setIsLoading(false);
        scrollToTop();
      }, (error) => {
        if (error?.response?.status === 401) {
          setIsLoading(false);
          setIsRecruiterAuthOpen(true);
          return;
        }
        console.log(error);
        setIsLoading(false);
      }));
  }, [dispatch, recruiterAccessToken, selectedFilters, pageSize, searchQuery, searchQueryProfiles]);

  const handleSkillClick = (skill_category) => {
    setSearchParams({ skill: skill_category });
  };

  const handleNlQueryChange = (e) => {
    const value = e.target.value;
    setNlQuery(value);

    if (!value.trim()) {
      dispatch({
        type: SET_SELECTED_FILTERS,
        payload: { ...selectedFilters, nlQueryFilters: [] },
      });
    }
  };

  function getRawQuery() {
    const search = location.search;
    if (!search) return null;
    const match = search.match(/[?&]query=([^&]*)/);
    if (match) {
      try {
        const protectedQuery = match[1].replace(/\+\+/g, "__PLUSPLUS__");
        const spaceReplaced = protectedQuery.replace(/\+/g, " ");
        const restored = spaceReplaced.replace(/__PLUSPLUS__/g, "++");
        return decodeURIComponent(restored);
      } catch (e) {
        return match[1];
      }
    }
    return searchParams.get("query");
  }

  useEffect(() => {
    if (applyFiltersFromLandingPage) {
      onApplyFilters();
      searchParams.delete("applyFilters");
      return;
    }
  }, [applyFiltersFromLandingPage, onApplyFilters, searchParams]);

  const fetchData = useCallback((query) => {
    setIsQueryReset(false);
    setSelectedRole(null);

    if (recruiterAccessToken) {
      setIsLoading(true);
      setHasSearched(true);

      dispatch(basicSearchProfiles(query, recruiterAccessToken, (response) => {
        setBasicSearchProfilesList(response?.data || []);
      }, (error) => {
        console.log(error);
      }));

      dispatch(
        fetchRelevantProfiles(
          query,
          recruiterAccessToken,
          (response) => {
            const profiles = Array.isArray(response?.data) ? response?.data : [];
            setSearchQueryProfiles(profiles);
            setRelevantProfiles(profiles);
            setTotalCount(response?.data?.length);
            dispatch({ type: SET_SELECTED_FILTERS, payload: { nlQueryFilters: response?.filters } });
            setIsLoading(false);
          },
          (error) => {
            if (error?.response?.status === 401) {
              setIsLoading(false);
              setIsRecruiterAuthOpen(true);
              return;
            }
            setSearchQueryProfiles([]);
            setRelevantProfiles([]);
            console.log(error);
            setIsLoading(false);
          }
        )
      );
      return;
    }

    if (!recruiterPublicNlSearchUsedRef.current) {
      setBasicSearchProfilesList([]);
      setIsLoading(true);
      setHasSearched(true);
      dispatch(
        getRelevantProfilesV2Public(
          query,
          (response) => {
            const profiles = Array.isArray(response?.data)
              ? response.data
              : Array.isArray(response?.profiles)
                ? response.profiles
                : [];
            setSearchQueryProfiles(profiles);
            setRelevantProfiles(profiles);
            setTotalCount(profiles.length);
            if (response?.filters !== undefined) {
              dispatch({ type: SET_SELECTED_FILTERS, payload: { nlQueryFilters: response.filters } });
            }
            setIsLoading(false);
          },
          () => {
            setSearchQueryProfiles([]);
            setRelevantProfiles([]);
            setIsLoading(false);
          },
        ),
      );
      return;
    }

    setIsLoading(false);
    setHasSearched(false);
    setIsRecruiterAuthOpen(true);
  }, [dispatch, recruiterAccessToken]);

  const fetchProfilesBySkill = useCallback((skill_category, pageNum = 1) => {
    setSelectedSkill(skill_category);
    setSelectedRole(null);
    setIsQueryReset(false);
    setSearchQueryProfiles([]);
    setIsLoading(true);
    dispatch(
      fetchProfilesBySkillCategory(recruiterUUID, skill_category, pageNum, pageSize, (response) => {
        setRelevantProfiles(response?.profiles || []);
        setTotalCount(response?.total_count);
        setIsLoading(false);
      }, (error) => {
        if (error?.response?.status === 401) {
          setIsLoading(false);
          setIsRecruiterAuthOpen(true);
          return;
        }
        console.log(error);
        setIsLoading(false);
      }
      ));
  }, [dispatch, pageSize, recruiterUUID]);

  useEffect(() => {
    if (searchQuery) {
      fetchData(searchQuery);
    }
    if (skill) {
      setPage(1);
      setSelectedSkill(skill);
      setSelectedRole(null);
      fetchProfilesBySkill(skill, 1);
    }
  }, [searchQuery, fetchData, fetchProfilesBySkill, skill]);

  const removeFilter = (category, filterToRemove) => {
    Mixpanel.track(Constants.Mixpanel.RECRUITER_RESULTS_PAGE_FILTER_REMOVED);
    const existingCategoryFilters = Array.isArray(selectedFiltersList?.[category]) ? selectedFiltersList[category] : [];
    const updatedFilters = existingCategoryFilters.filter(
      (item) => item !== filterToRemove
    );
    const nextFilters = { ...selectedFiltersList, [category]: updatedFilters };
    dispatch({
      type: SET_SELECTED_FILTERS,
      payload: nextFilters,
    });

    if (searchQuery) {
      onApplyFilters(nextFilters);
    }
  };


  useEffect(() => {
    if (!skill) return;

    dispatch(fetchRolesBySkill(skill, (response) => {
      setCandidateRoles(response);
    }, (error) => {
      console.log(error);
    }));
  }, [dispatch, skill]);

  const handleFilterByRole = (skill, subSkill) => {
    const isTogglingOff = selectedRole === subSkill;
    const nextRole = isTogglingOff ? null : subSkill;

    setSelectedRole(nextRole);
    setPage(1);
    setIsLoading(true);
    setIsMoreLoading(false);

    const onSuccess = (response) => {
      setRelevantProfiles(response?.profiles || []);
      setTotalCount(response?.total_count);
      setIsLoading(false);
      scrollToTop();
    };

    const onError = (error) => {
      if (error?.response?.status === 401) {
        setIsLoading(false);
        setIsRecruiterAuthOpen(true);
        return;
      }
      console.log(error);
      setIsLoading(false);
    };

    // Clicking the selected role again clears the role filter
    // and returns to the base "searched category" results.
    if (isTogglingOff) {
      dispatch(fetchProfilesBySkillCategory(recruiterUUID, skill, 1, pageSize, onSuccess, onError));
      return;
    }

    dispatch(filterByRole(skill, subSkill, 1, pageSize, recruiterUUID, onSuccess, onError));
  };

  useEffect(() => {
    if (relevantProfiles?.length === 0 && hasSearched) {
      setTimeout(() => {
        setHasSearched(false);
      }, 3000);
    }
  }, [relevantProfiles, hasSearched]);

  const featuredSkills = [
    "AI, ML & Data Science",
    "Software Engineering & Development",
    "Cloud, DevOps & Cybersecurity",
    "Product & Design",
    "Marketing",
    "Sales & Business Development",
    "Operations & Administration",
    "Project Management & Agile",
    "Business Analysis & Consulting",
    "Customer Support & Success",
    "HR & People",
    "Specialized Engineering",
    "Finance & Accounting",
    "Leadership & Executives",
    "Students & Freshers",
    "Others",
  ];

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  return (
    <div
      className="min-h-screen font-sans text-white flex flex-col items-center"
      style={{
        background: "radial-gradient(55.67% 55.67% at 50% 44.33%, #002842 0%, #001C2C 100%)"
      }}
    >
      <div className="w-full max-w-6xl mx-auto font-sans text-white flex flex-col items-center gap-4">

        <StickyHeader onFilterClick={() => setIsFilterOpen(true)}
          setIsProfileMenuOpen={setIsProfileMenuOpen}
          isProfileMenuOpen={isProfileMenuOpen}
          onApplyFilters={onApplyFilters}
          setIsRecruiterAuthOpen={setIsRecruiterAuthOpen}
          showStickyFilters={showStickyFilters}
        />

        {/* Titles */}
        {/* {isQueryReset ? ( */}
        <div className="w-full flex-col items-center gap-4 md:gap-6 pt-4 md:pt-8 px-4 md:px-0 flex">

          <h2 className="flex w-full font-inter justify-center text-2xl items-center font-bold text-white">{isLoading ? "Matching Your Requirements..." : relevantProfiles.length > 0 ? `${totalCount || ''} Top Candidates Matched To Your Requirements` : ""}</h2>

          <form onSubmit={(e) => {
            e.preventDefault();
            setSearchParams({ query: nlQuery });
          }}
            className="relative w-full max-w-2xl">
            <input
              type="text"
              value={nlQuery}
              onChange={handleNlQueryChange}
              placeholder={width < 768 ? "Enter Hiring Requirements" : "Paste JD or Search by skill/role, e.g. Data Scientist, Product Mgmt"}
              className="w-full rounded-xl md:rounded-2xl border-2 border-[#8CC63F] bg-transparent py-3 pl-4 pr-12 text-sm md:text-base md:py-4 md:pl-6 md:pr-14 text-[#8CC63F] placeholder-[#8CC63F]/50 focus:placeholder-[#8CC63F]/0 shadow-md shadow-[#8CC63F] focus:outline-none focus:ring-1 focus:ring-[#8CC63F]"
            />
            <img src="https://assets.learntube.ai/files/halloffame/recruiter/tabler_ai.svg" alt="AI icon" className="absolute right-8 top-1/2 -translate-y-1/2 p-2 text-[#8CC63F] transition-colors md:right-12" />
            <button
              type="submit"
              className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 p-2 text-[#8CC63F] transition-colors disabled:opacity-50"
              disabled={isLoading}>
              {isLoading ? (
                <div className="h-4 w-4 md:h-5 md:w-5 animate-spin rounded-full border-2 border-[#8CC63F] border-t-transparent" />
              ) : (
                <Search className="w-5 h-5 md:w-6 md:h-6" />
              )}
            </button>
          </form>
        </div>
        {/* ) : (
          <div className="w-full p-4 flex flex-col items-center gap-8">
            <h2 className="flex w-full font-inter justify-center text-2xl items-center font-bold text-white">{isLoading ? "Matching Your Requirements..." : relevantProfiles.length > 0 ? `${totalCount || ''} Top Candidates Matched To Your Requirements` : ""}</h2>

            <div className="flex w-full font-inter justify-between text-[12px] items-center text-white border border-[#7FC241] rounded-lg px-4 py-2">
              <div className={`flex flex-col pr-2 items-center justify-center font-medium text-[#7FC241] gap-2 ${searchQuery?.length < 30 ? 'text-xl' : 'text-sm'}`}>
                <p>{searchQuery || selectedSkill || "Your Filtered Profiles"}</p>
              </div>
              <button className="text-[#7FC241]" onClick={handleQueryReset}>
                <X size={searchQuery?.length < 30 ? 20 : 16} />
              </button>
            </div>
          </div>)} */}

        {/* Scrollable Skills List */}
        <div className="flex w-full items-center justify-center px-4">
          {candidateRoles?.length > 0 && skill && <h3 className="flex w-full font-inter font-bold text-white">Filter By Role Category</h3>}
        </div>
        {candidateRoles?.length > 0 && skill && (
          <div className="flex w-full overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] scrollbar-hide">

            <div className="flex flex-col gap-2 md:gap-3 px-1 md:px-4 w-max">
              {[
                candidateRoles.filter((_, i) => candidateRoles.length <= 7 || i % 2 === 0),
                candidateRoles.length > 5 ? candidateRoles.filter((_, i) => i % 2 !== 0) : []
              ].map((rowRoles, rowIndex) => (
                rowRoles.length > 0 && (
                  <div key={rowIndex} className="flex gap-2 md:gap-3">
                    {rowRoles.map((subSkill, index) => (
                      (() => {
                        const isSelected = selectedRole === subSkill;
                        return (
                          <button
                            key={`${rowIndex}-${index}`}
                            type="button"
                            onClick={() => handleFilterByRole(skill, subSkill)}
                            aria-pressed={isSelected}
                            className={`flex items-center gap-1.5 md:gap-2 rounded-full border px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm text-white transition-colors whitespace-nowrap ${isSelected
                              ? "border-[#406AFF] bg-[#406AFF]/25 shadow-sm shadow-[#406AFF]/30"
                              : "border-[#406AFF]/50 bg-[#10213F] hover:bg-[#133145]"
                              }`}
                          >
                            <TrendingUp className={`w-3 h-3 md:w-4 md:h-4 ${isSelected ? "text-[#FFFFFF]" : "text-[#8CC63F]"}`} />
                            <span>{subSkill}</span>
                          </button>
                        );
                      })()
                    ))}
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        <div className="w-full p-4 flex flex-col items-center gap-8 md:gap-12">
          <div className="mx-auto w-full flex flex-col items-center gap-4">
            {/* selected filters section */}
            <div className="flex w-full flex-col items-center gap-4 px-4 md:px-0 md:flex-row md:justify-between">
              {!isQueryReset &&
                <div className="hidden md:flex w-1/3 items-center ">
                  <h2 className="flex w-full font-inter text-2xl font-bold text-white">Your Top Candidates</h2>
                </div>}
              <div className="flex flex-wrap items-center justify-center gap-3 md:justify-start">
                {selectedFiltersList &&
                  Object.entries(selectedFiltersList).map(([category, filters]) =>
                    filters.map((filter, index) => (
                      <div
                        key={`${category}-${index}`}
                        className="flex items-center gap-2 rounded-xl bg-[#133145] px-4 py-2 text-sm font-medium text-white"
                      >
                        <span>{filter}</span>
                        <button
                          onClick={() => removeFilter(category, filter)}
                          className="text-[#5B8FB9] hover:text-white"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))
                  )}
                <button
                  ref={filterButtonRef}
                  onClick={() => {
                    Mixpanel.track(Constants.Mixpanel.RECRUITER_RESULTS_PAGE_VIEW_FILTERS_CLICKED);
                    if (!recruiterAccessToken) {
                      setIsRecruiterAuthOpen(true);
                      return;
                    }
                    setIsFilterOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-xl bg-[#133145] px-4 py-2 text-sm font-medium text-white hover:bg-[#1A3F5B] transition-colors"
                >
                  <Filter size={16} className="text-[#5B8FB9]" />
                  <span>Filter</span>
                </button>
              </div>

              {!isQueryReset &&
                <div className="flex w-full items-center md:hidden">
                  <h2 className="flex w-full font-inter justify-center text-2xl items-center font-bold text-white">Your Top Candidates</h2>
                </div>}
            </div>

            {/* Content Section */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center w-60 h-60">
                <div className="w-full h-48 relative">
                  <ProfilesLoader />
                </div>
              </div>
            ) : (
              <>
                {/* show first 4 candidates */}
                <div className="grid w-full grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6 justify-items-center">
                  {relevantProfiles.length > 0
                    ? relevantProfiles.slice(0, 6).map((profile, index) => (
                      <CandidatesCard key={profile.id || index} candidates={profile} index={index} isWishlisted={profile?.is_wishlisted} onLogin={() => setIsRecruiterAuthOpen(true)} matchScore={profile?.match_score} />
                    ))
                    : (
                      <div className={`col-span-full py-12 text-center text-gray-400 ${isQueryReset ? 'hidden' : 'flex'}`}>
                        No profiles found matching your criteria.
                      </div>
                    )}
                </div>

                {!isQueryReset && relevantProfiles?.length > 0 &&
                  <Locations
                    onApplyFilters={onApplyFilters}
                    onLogin={() => setIsRecruiterAuthOpen(true)}
                  />}

                <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                  {relevantProfiles?.slice(6).map((card, index) => (
                    <RowCandidateCard
                      key={index + 6}
                      rank={card?.match_score}
                      name={card?.profile?.full_name?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
                      title={card?.experience?.job_title || card?.education?.degree_name}
                      pastTitle={card?.individual_scores?.evidence_breakdown?.experience_evidence?.[0]?.job_title || ''}
                      skillEvidence={card?.individual_scores?.evidence_breakdown?.profile_evidence?.primary_found}
                      company={card?.experience?.company_name || card?.education?.institution_name}
                      isWishlisted={card?.is_wishlisted}
                      candidateEmail={card?.id_email}
                      profilePicture={card?.profile_photo}
                      onLogin={() => setIsRecruiterAuthOpen(true)}
                      experience={card?.additional_profile_info?.total_experience_months}
                      isBasicSearch={false}
                    />
                  ))}
                </div>

                {/*  more - Pagination */}
                {totalCount > 20 && skill && (
                  <div className="flex w-full items-center justify-center py-4">
                    <button onClick={seeMoreProfiles} disabled={isMoreLoading} className="inline-flex cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg border border-solid border-[#006fbc] bg-transparent px-4 py-2 transition-colors hover:bg-[#006fbc]/10 disabled:opacity-50 disabled:cursor-not-allowed">
                      <span className="mt-[-1.00px] flex w-fit items-center justify-center text-center font-['Inter',Helvetica] text-sm font-bold leading-[21px] tracking-[0] text-[#006fbc]">
                        {isMoreLoading ? "Loading..." : "See 20 more"}
                      </span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {basicSearchProfilesList?.length > 0 && <h3 className="text-white text-lg font-bold">{isLoading ? 'While we query the top-fit candidates, explore these promising profiles.' : 'Basic Search Results'}</h3>}
          {basicSearchProfilesList?.length > 0 && (
            <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
              {basicSearchProfilesList.map((card, index) => (
                <RowCandidateCard
                  key={index}
                  rank={card?.match_score}
                  name={card?.profile?.full_name?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
                  title={card?.experience?.job_title || card?.education?.degree_name}
                  pastTitle={card?.individual_scores?.evidence_breakdown?.experience_evidence?.[0]?.job_title || ''}
                  skillEvidence={card?.individual_scores?.evidence_breakdown?.profile_evidence?.primary_found}
                  company={card?.experience?.company_name || card?.education?.institution_name}
                  isWishlisted={card?.is_wishlisted}
                  candidateEmail={card?.id_email}
                  profilePicture={card?.profile_photo}
                  onLogin={() => setIsRecruiterAuthOpen(true)}
                  experience={card?.additional_profile_info?.total_experience_months}
                  isBasicSearch={true}
                />
              ))}
            </div>
          )}

          <div className="px-0 py-4 w-full gap-4 rounded-3xl flex flex-col items-center justify-center">
            <h2 className="text-2xl text-center text-white font-bold">Looking for Other Talent?</h2>
            <div className="grid w-full grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-4 justify-items-center">
              <FeaturedLeaderboard
                featuredSkills={featuredSkills}
                handleViewLeaderboard={handleSkillClick}
                scrollToTop={scrollToTop}
              />
            </div>
            <div className="w-full flex items-center justify-center px-2 py-4">
              <button className="bg-[#406AFF]/10 text-white px-4 py-2 rounded w-full flex flex-row items-center justify-center" onClick={() => {
                Mixpanel.track(Constants.Mixpanel.RECRUITER_RESULTS_PAGE_OTHER_LEADERBOARDS_CLICKED)
                setIsSkillModalOpen(true)
              }}>
                <ChartNoAxesColumnIncreasing className="w-4 h-4 mr-2" color="#7896FF" />
                <span className="text-[#7896FF] text-sm font-sans font-bold">More Leaderboards</span>
              </button>
            </div>
          </div>

          <div className="flex w-full items-center justify-center gap-2 p-4 md:gap-1.5 md:p-3">
            <img
              className="h-5 w-5 md:h-6 md:w-6"
              alt="Email"
              src="https://assets.learntube.ai/files/halloffame/recruiter/icroundemail.svg"
            />
            <div className="h-5 w-24 text-xs font-normal leading-tight tracking-[0] text-white [font-family:'Inter',Helvetica] md:h-[21px] md:w-[116px] md:leading-[16.8px]">
              <a
                href="mailto:hello@careerninja.in"
                rel="noopener noreferrer"
                target="_blank">
                <span className="underline">hello@careerninja.in</span>
              </a>
            </div>
          </div>
          <ListofCategories
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onApplyFilters={onApplyFilters}
          />
          <RecruiterAuth isOpen={isRecruiterAuthOpen}
            onClose={() => setIsRecruiterAuthOpen(false)} />
          <RecruiterDetails isOpen={isRecruiterDetailsOpen}
            onClose={() => setIsRecruiterDetailsOpen(false)} />
          <SkillModal isOpen={isSkillModalOpen}
            onClose={() => setIsSkillModalOpen(false)}
            onApplyFilters={onApplyFilters} />
        </div>
      </div>
    </div>
  );
};

export default LeaderboardProfiles;
